import { api } from '../../api/client';

export type Role = 'viewer' | 'operator' | 'engineer' | 'manager' | 'admin';

export interface UserProfile {
  id: string;
  email: string;
  display_name: string;
  role: Role;
  locale: string;
  permissions: string[];
  mfa_enrolled: boolean;
  mfa_required: boolean;
}

export interface LoginResponse {
  mfa_required: boolean;
  mfa_token: string | null;
  csrf_token: string | null;
  /** Sent only to the Android application, which cannot hold a cookie for the
   *  server's host. Always null in a browser — see `platform/host`. */
  session_token: string | null;
  user: UserProfile | null;
}

export interface Passkey {
  id: string;
  nickname: string | null;
  created_at: string;
  last_used_at: string | null;
  transports: string[] | null;
  /** Whether the key stores the account inside itself, and can therefore be
   *  offered before an address is typed. Null when it never said. */
  discoverable: boolean | null;
}

export interface SecurityStatus {
  totp_enrolled: boolean;
  recovery_codes_left: number;
  passkeys: Passkey[];
}

export interface SessionRow {
  id: string;
  created_at: string;
  last_seen_at: string | null;
  expires_at: string;
  ip: string | null;
  user_agent: string | null;
  client: string;
  amr: string[];
  is_label_station: boolean;
  current: boolean;
}

export const login = (email: string, password: string) =>
  api.post<LoginResponse>('/auth/login', { email, password });

export const loginTotp = (mfaToken: string, code: string) =>
  api.post<LoginResponse>('/auth/login/totp', { mfa_token: mfaToken, code });

export const loginRecovery = (mfaToken: string, code: string) =>
  api.post<LoginResponse>('/auth/login/recovery-code', { mfa_token: mfaToken, code });

export const logout = () => api.post<void>('/auth/logout');

export const fetchMe = () => api.get<UserProfile>('/auth/me');

export const changePassword = (currentPassword: string, newPassword: string) =>
  api.post<void>('/auth/password', {
    current_password: currentPassword,
    new_password: newPassword,
  });

export const fetchSecurity = () => api.get<SecurityStatus>('/auth/security');

export const startTotp = () => api.post<{ secret: string; uri: string }>('/auth/totp/start');

export const confirmTotp = (code: string) =>
  api.post<{ codes: string[] }>('/auth/totp/confirm', { code });

export const disableTotp = (code: string) => api.post<void>('/auth/totp/disable', { code });

export const resetRecoveryCodes = () => api.post<{ codes: string[] }>('/auth/recovery-codes');

export const fetchSessions = () => api.get<SessionRow[]>('/auth/sessions');

export const revokeSession = (id: string) => api.delete<void>(`/auth/sessions/${id}`);

// --- passkeys --------------------------------------------------------------

interface Challenge {
  challenge_id: string;
  public_key: Record<string, unknown>;
}

/**
 * WebAuthn speaks ArrayBuffers; JSON does not, so every identifier and challenge
 * crosses the wire base64url-encoded and has to be turned back here. The
 * browser's own `parseCreationOptionsFromJSON` does exactly this, and is recent
 * enough that relying on it would rule out browsers this workshop has.
 */
function fromBase64Url(value: string): Uint8Array {
  const padded = value.replace(/-/g, '+').replace(/_/g, '/');
  const binary = atob(padded.padEnd(padded.length + ((4 - (padded.length % 4)) % 4), '='));
  return Uint8Array.from(binary, (char) => char.charCodeAt(0));
}

function toBase64Url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

interface PublicKeyJson {
  challenge: string;
  user?: { id: string; name: string; displayName: string };
  excludeCredentials?: { id: string; type: string; transports?: string[] }[];
  allowCredentials?: { id: string; type: string; transports?: string[] }[];
  [key: string]: unknown;
}

function decodeOptions(options: Record<string, unknown>) {
  const json = options as unknown as PublicKeyJson;
  const decoded: Record<string, unknown> = { ...json, challenge: fromBase64Url(json.challenge) };

  if (json.user) decoded.user = { ...json.user, id: fromBase64Url(json.user.id) };
  for (const key of ['excludeCredentials', 'allowCredentials'] as const) {
    const list = json[key];
    if (list) decoded[key] = list.map((item) => ({ ...item, id: fromBase64Url(item.id) }));
  }
  return decoded as unknown as PublicKeyCredentialCreationOptions &
    PublicKeyCredentialRequestOptions;
}

function encodeCredential(credential: PublicKeyCredential): Record<string, unknown> {
  const response = credential.response as AuthenticatorAttestationResponse &
    AuthenticatorAssertionResponse;

  const payload: Record<string, unknown> = {
    clientDataJSON: toBase64Url(response.clientDataJSON),
  };

  if (response.attestationObject) {
    payload.attestationObject = toBase64Url(response.attestationObject);
    // What the authenticator says about how it is reachable — USB, NFC, the
    // platform itself. Stored so the list of keys can say which is which.
    payload.transports = response.getTransports?.() ?? [];
  } else {
    payload.authenticatorData = toBase64Url(response.authenticatorData);
    payload.signature = toBase64Url(response.signature);
    payload.userHandle = response.userHandle ? toBase64Url(response.userHandle) : null;
  }

  return {
    id: credential.id,
    rawId: toBase64Url(credential.rawId),
    type: credential.type,
    clientExtensionResults: credential.getClientExtensionResults(),
    response: payload,
  };
}

export const passkeysSupported = () =>
  typeof window !== 'undefined' && !!window.PublicKeyCredential;

export async function registerPasskey(nickname: string | null): Promise<Passkey> {
  const challenge = await api.post<Challenge>('/auth/webauthn/register/options');
  const credential = (await navigator.credentials.create({
    publicKey: decodeOptions(challenge.public_key),
  })) as PublicKeyCredential | null;

  if (!credential) throw new Error('cancelled');

  return api.post<Passkey>('/auth/webauthn/register', {
    challenge_id: challenge.challenge_id,
    credential: encodeCredential(credential),
    nickname,
  });
}

export async function signInWithPasskey(email: string | null): Promise<LoginResponse> {
  const challenge = await api.post<Challenge>('/auth/webauthn/login/options', { email });
  const credential = (await navigator.credentials.get({
    publicKey: decodeOptions(challenge.public_key),
  })) as PublicKeyCredential | null;

  if (!credential) throw new Error('cancelled');

  return api.post<LoginResponse>('/auth/webauthn/login', {
    challenge_id: challenge.challenge_id,
    credential: encodeCredential(credential),
  });
}

export const renamePasskey = (id: string, nickname: string) =>
  api.patch<Passkey>(`/auth/webauthn/credentials/${id}`, { nickname });

export const removePasskey = (id: string) => api.delete<void>(`/auth/webauthn/credentials/${id}`);
